import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { usePageContext } from '../../context/PageContext';
import {
  LayoutDashboard,
  AlertTriangle,
  Clock,
  BookOpen,
  Mail,
  Code,
  Settings,
  LogOut,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeft,
  Bell,
  Users,
  GraduationCap,
  FileText,
  HelpCircle,
  CreditCard,
  Library as LibraryIcon,
  Download,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  LATENESS_CATEGORY,
  UNDER_REVIEW_STATUSES,
  CLOSED_STATUSES,
} from '../../lib/issueCounting';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import AssistantWidget from '../assistant/AssistantWidget';
import TrialBanner from './TrialBanner';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
  { icon: AlertTriangle, label: 'Flagged Issues', path: '/issues', hasBadge: true },
  { icon: Clock, label: 'Punctuality', path: '/punctuality' },
  { icon: BookOpen, label: 'Lessons', path: '/lessons' },
  { icon: Users, label: 'Tutors', path: '/tutors' },
  { icon: GraduationCap, label: 'Students', path: '/students' },
  { icon: LibraryIcon, label: 'Library', path: '/library' },
];

const bottomNavItems = [
  { icon: Download, label: 'Export', path: '/export' },
  { icon: Mail, label: 'Email Reports', path: '/email-notifications' },
  { icon: CreditCard, label: 'Billing', path: '/billing' },
  { icon: Code, label: 'API', path: '/api-workspace' },
  { icon: Code, label: 'Developer', path: '/developer' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

const NavItem = ({ icon: Icon, label, path, isActive, collapsed, isDark, badgeCount }) => {
  const content = (
    <NavLink
      to={path}
      className={`
        flex items-center rounded-lg text-sm transition-all duration-300 ease-in-out relative overflow-hidden
        ${isActive
          ? isDark
            ? 'bg-[#18181b] text-white'
            : 'bg-[#e8e8e8] text-[#1a1a1a] font-medium'
          : isDark
            ? 'text-[#a1a1aa] hover:text-white hover:bg-[#121212]'
            : 'text-[#555555] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'
        }
      `}
      style={{ paddingLeft: '12px', paddingRight: '12px', paddingTop: '10px', paddingBottom: '10px' }}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 relative">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        {collapsed && badgeCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full bg-amber-500 text-[8px] font-bold text-white flex items-center justify-center">{badgeCount > 9 ? '9+' : badgeCount}</span>
        )}
      </div>
      <span className={`ml-3 whitespace-nowrap transition-all duration-300 ease-in-out ${collapsed ? 'opacity-0 w-0 max-w-0 ml-0 overflow-hidden' : 'opacity-100'}`}>
        {label}
      </span>
      {!collapsed && badgeCount > 0 && (
        <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500">{badgeCount}</span>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className={isDark ? 'bg-[#18181b] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}>
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
};

const AppShell = ({ children }) => {
  const { user, logout, developerAccess, isStaff } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { subtitle } = usePageContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem('tutortracer_sidebar_collapsed') === 'true';
  });

  const isDark = theme === 'dark';
  const [recentLessons, setRecentLessons] = useState([]);
  const [openIssueCount, setOpenIssueCount] = useState(0);

  const loadRecentLessons = useCallback(async () => {
    if (!user) return;
    try {
      const { data: userOrg } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      if (!userOrg) return;
      const { data } = await supabase
        .from('sessions')
        .select('id, tutor_name, student_name, lesson_timestamp, subject_covered, is_processed_v2')
        .eq('organization_id', userOrg.organization_id)
        .order('lesson_timestamp', { ascending: false })
        .limit(8);
      setRecentLessons(data || []);
    } catch (e) { /* ignore */ }
  }, [user]);

  const loadOpenIssueCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data: userOrg } = await supabase
        .from('user_organizations')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      if (!userOrg) return;

      // The badge must equal countOpenIssues() from lib/issueCounting.js — that
      // file is the source of truth for what a "flagged issue" is. Fetching
      // every row just to count them would be wasteful for a sidebar badge, so
      // the three rules are mirrored as server-side filters instead. Each
      // .or() is a separate PostgREST predicate and they AND together.
      //
      //   1. tutor_lateness is never an issue (Punctuality owns it). A NULL
      //      category is not lateness, so it still counts — plain .neq() would
      //      drop NULLs, hence the explicit is-null branch.
      //   2. Guardrail-suppressed risks are never issues. Suppression is the
      //      LLM-stamped filter_status='suppressed'; any other value (including
      //      NULL) is visible — never keyword matching.
      //   3. Open = anything not under review and not closed, so an unknown
      //      status fails visible rather than silently vanishing. The status
      //      lists come from issueCounting.js so the two cannot drift.
      const notOpenStatuses = [...UNDER_REVIEW_STATUSES, ...CLOSED_STATUSES].join(',');
      const { count } = await supabase
        .from('risks_v2')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', userOrg.organization_id)
        .or(`category.is.null,category.neq.${LATENESS_CATEGORY}`)
        .or('filter_status.is.null,filter_status.neq.suppressed')
        .or(`status.is.null,status.not.in.(${notOpenStatuses})`);
      setOpenIssueCount(count || 0);
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    loadRecentLessons();
    loadOpenIssueCount();
    const interval = setInterval(() => { loadRecentLessons(); loadOpenIssueCount(); }, 30000);
    return () => clearInterval(interval);
  }, [loadRecentLessons, loadOpenIssueCount]);

  // Drag-to-resize sidebar (expanded state only); width remembered per browser.
  const SIDEBAR_MIN = 200, SIDEBAR_MAX = 420;
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('tutortracer_sidebar_width') || '', 10);
      return Number.isFinite(v) ? Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, v)) : 256;
    } catch { return 256; }
  });
  const [resizing, setResizing] = useState(false);
  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = sidebarWidth;
    setResizing(true);
    const onMove = (ev) => setSidebarWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startW + ev.clientX - startX)));
    const onUp = (ev) => {
      const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, startW + ev.clientX - startX));
      try { localStorage.setItem('tutortracer_sidebar_width', String(w)); } catch {}
      setResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const toggleCollapse = () => {
    const newValue = !collapsed;
    setCollapsed(newValue);
    localStorage.setItem('tutortracer_sidebar_collapsed', String(newValue));
  };

  const currentPageLabel = [...navItems, ...bottomNavItems].find(item =>
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  )?.label || 'TutorTracer';

  return (
    <TooltipProvider>
      <div className={`min-h-screen flex ${isDark ? 'bg-[#09090b]' : 'bg-[#fafafa]'}`}>
        {/* Sidebar */}
        <aside
          style={{ width: collapsed ? 64 : sidebarWidth }}
          className={`relative shrink-0 border-r flex flex-col h-screen sticky top-0 ${resizing ? '' : 'transition-all duration-300 ease-in-out'} overflow-x-hidden overflow-y-auto ${isDark ? 'border-[#27272a] bg-[#0d0d0f]' : 'border-[#e5e5e5] bg-white'}`}>
          {!collapsed && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              title="Drag to resize"
              onMouseDown={startResize}
              onDoubleClick={() => { setSidebarWidth(256); try { localStorage.setItem('tutortracer_sidebar_width', '256'); } catch {} }}
              className={`absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 transition-colors ${resizing ? 'bg-[#586f8d]/60' : 'hover:bg-[#586f8d]/40'}`}
            />
          )}
          {/* Logo — h-[60px] matches top nav bar so borders align */}
          <div className={`h-[60px] px-4 border-b flex items-center shrink-0 transition-all duration-300 ease-in-out ${collapsed ? 'justify-center' : ''} ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            <NavLink to="/" aria-label="Go to dashboard" className="cursor-pointer">
            {collapsed ? (
              <div className="w-7 h-7 overflow-hidden flex-shrink-0">
                <img
                  src={isDark ? '/tutortracer_logowithtext_white_trpt.png' : '/tutortracer_logowithtext_black_trpt.png'}
                  alt="TutorTracer"
                  className="h-7 w-auto max-w-none"
                  style={{ objectFit: 'cover', objectPosition: 'left center' }}
                />
              </div>
            ) : (
              <img
                src={isDark ? '/tutortracer_logowithtext_white_trpt.png' : '/tutortracer_logowithtext_black_trpt.png'}
                alt="TutorTracer"
                className="h-8 w-auto"
                style={{ objectFit: 'contain' }}
              />
            )}
            </NavLink>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 overflow-y-auto overflow-x-hidden">
            <div className={`px-2 mb-2 text-xs font-medium uppercase tracking-wider transition-all duration-300 ease-in-out ${
              collapsed ? 'opacity-0 h-0 mb-0' : 'opacity-100'
            } ${isDark ? 'text-[#52525b]' : 'text-[#999999]'}`}>
              Overview
            </div>
            <div className="space-y-1">
              {navItems.map(({ icon, label, path, hasBadge }) => (
                <NavItem
                  key={path}
                  icon={icon}
                  label={label}
                  path={path}
                  isActive={path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)}
                  collapsed={collapsed}
                  isDark={isDark}
                  badgeCount={hasBadge ? openIssueCount : 0}
                />
              ))}
            </div>
          </nav>

          {/* Bottom Section */}
          <div className={`p-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
            {/* Collapse/Expand Toggle */}
            <button
              onClick={toggleCollapse}
              className={`w-full mb-1 flex items-center rounded-lg text-sm transition-all duration-300 ease-in-out ${isDark ? 'text-[#52525b] hover:text-white hover:bg-[#121212]' : 'text-[#888888] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'}`}
              style={{ paddingLeft: '12px', paddingRight: '12px', paddingTop: '10px', paddingBottom: '10px' }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {collapsed ? <PanelLeft className="w-4 h-4" strokeWidth={1.5} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />}
              </div>
              <span className={`ml-3 whitespace-nowrap transition-all duration-300 ease-in-out ${collapsed ? 'opacity-0 w-0 max-w-0 ml-0 overflow-hidden' : 'opacity-100'}`}>
                Collapse
              </span>
            </button>

            {/* Below-divider nav items */}
            <div className="space-y-1 mb-3">
              {bottomNavItems.filter(item => (isStaff || item.path !== '/developer') && (developerAccess || item.path !== '/email-notifications')).map(({ icon, label, path }) => (
                <NavItem
                  key={path}
                  icon={icon}
                  label={label}
                  path={path}
                  isActive={location.pathname.startsWith(path)}
                  collapsed={collapsed}
                  isDark={isDark}
                />
              ))}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`w-full flex items-center rounded-lg text-sm transition-all duration-300 ease-in-out ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#121212]' : 'text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'}`}
              style={{ paddingLeft: '12px', paddingRight: '12px', paddingTop: '10px', paddingBottom: '10px' }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {isDark ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
              </div>
              <span className={`ml-3 whitespace-nowrap transition-all duration-300 ease-in-out ${collapsed ? 'opacity-0 w-0 max-w-0 ml-0 overflow-hidden' : 'opacity-100'}`}>
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-screen min-w-0">
          {/* Top Bar */}
          <header className={`h-[60px] border-b flex items-center justify-between px-6 sticky top-0 z-40 ${
            isDark
              ? 'border-[#27272a] bg-[#09090b]/95'
              : 'border-[#e5e5e5] bg-[#fafafa]/95'
          } backdrop-blur-sm`}>
            <div className="flex items-center gap-2 min-w-0">
              <h2 className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                {currentPageLabel}
                {subtitle && (
                  <span className={`font-normal ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    {' '}&rsaquo;{' '}
                  </span>
                )}
                {subtitle && (
                  <span className={`font-normal ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {subtitle}
                  </span>
                )}
              </h2>
            </div>

            <div className="flex items-center gap-1">
              {/* Support */}
              <button
                onClick={() => navigate('/support')}
                className={`px-3 py-1.5 text-sm transition-colors rounded-md flex items-center gap-1.5 ${
                  isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
                Help
              </button>
              {/* Settings */}
              <button
                onClick={() => navigate('/settings')}
                className={`p-2 transition-colors rounded-md ${
                  isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
                }`}
              >
                <Settings className="h-4.5 w-4.5" strokeWidth={1.5} />
              </button>
              {/* Notifications */}
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className={`p-2 transition-colors rounded-md relative ${
                      isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666666] hover:text-[#1a1a1a]'
                    }`}
                  >
                    <Bell className="h-4.5 w-4.5" strokeWidth={1.5} />
                    <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#586f8d]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  side="bottom"
                  className={`w-80 p-0 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
                >
                  <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Live Lessons</span>
                    <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Auto-refreshes</span>
                  </div>
                  <div className={`max-h-80 overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
                    {recentLessons.length === 0 ? (
                      <div className={`p-6 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No recent lessons</div>
                    ) : recentLessons.map(l => {
                      const isNew = (Date.now() - new Date(l.lesson_timestamp).getTime()) < 3600000;
                      return (
                        <button
                          key={l.id}
                          onClick={() => navigate(`/lessons/${l.id}`)}
                          className={`w-full px-4 py-3 text-left transition-colors ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f5f5f5]'}`}
                        >
                          <div className="flex items-center gap-2">
                            {isNew && <span className="w-1.5 h-1.5 rounded-full bg-[#586f8d] flex-shrink-0" />}
                            <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{l.tutor_name}</span>
                            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>→</span>
                            <span className={`text-sm truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{l.student_name}</span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{l.subject_covered || 'No subject'}</span>
                            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                              {new Date(l.lesson_timestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                          {!l.is_processed_v2 && (
                            <span className={`text-xs mt-1 inline-block ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>Processing...</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className={`px-4 py-2 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                    <button
                      onClick={() => navigate('/lessons')}
                      className={`text-xs w-full text-center ${isDark ? 'text-[#7662ac] hover:text-white' : 'text-[#586f8d] hover:text-[#1a1a1a]'}`}
                    >
                      View all lessons
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
              {/* Profile Avatar */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="ml-1 w-8 h-8 rounded-full accent-gradient flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-medium">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  side="bottom"
                  className={`w-auto min-w-56 max-w-80 p-2 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
                >
                  <div className="px-3 py-2 mb-1">
                    <p className={`text-sm truncate ${isDark ? 'text-[#a1a1aa]' : 'text-[#666666]'}`} title={user?.email}>{user?.email}</p>
                  </div>
                  <div className={`border-t pt-1 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/settings')}
                      className={`w-full justify-start ${isDark ? 'text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'text-[#666666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={logout}
                      className="w-full justify-start text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Log out
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-6 min-w-0">
            <TrialBanner />
            {children}
          </main>
        </div>

        {/* Floating assistant — fixed bottom-right, present on every page. */}
        <AssistantWidget />
      </div>
    </TooltipProvider>
  );
};

export default AppShell;
